import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, FlaskConical, Mail, Building2, Trash2, RefreshCw, Download, LogOut, FileText, Plus, Phone, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminLogin from './AdminLogin';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState({ appointments: 0, lab_orders: 0, contact_inquiries: 0, corporate_inquiries: 0, reports: 0 });
  const [appointments, setAppointments] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [contactInquiries, setContactInquiries] = useState([]);
  const [corporateInquiries, setCorporateInquiries] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportForm, setReportForm] = useState({ patient_phone: '', patient_name: '', report_name: '', report_date: '', report_url: '' });

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => { setUser(res.data); setChecking(false); })
      .catch(() => { setUser(null); setChecking(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, apptRes, labRes, contactRes, corpRes, reportRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/lab-orders`, { withCredentials: true }),
        axios.get(`${API}/contact-inquiries`, { withCredentials: true }),
        axios.get(`${API}/corporate-inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/reports`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setAppointments(apptRes.data);
      setLabOrders(labRes.data);
      setContactInquiries(contactRes.data);
      setCorporateInquiries(corpRes.data);
      setReports(reportRes.data);
    } catch (err) {
      if (err.response?.status === 401) { setUser(null); return; }
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const handleDelete = async (type, id) => {
    try {
      await axios.delete(`${API}/${type}/${id}`, { withCredentials: true });
      toast.success('Record deleted');
      fetchAll();
    } catch (err) {
      if (err.response?.status === 401) { setUser(null); return; }
      toast.error('Failed to delete');
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    toast.success('Logged out');
  };

  const handleExport = () => window.open(`${API}/export/lab-tests`, '_blank');

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/reports`, reportForm, { withCredentials: true });
      toast.success('Report uploaded successfully');
      setReportForm({ patient_phone: '', patient_name: '', report_name: '', report_date: '', report_url: '' });
      setShowReportForm(false);
      fetchAll();
    } catch (err) {
      toast.error('Failed to upload report');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return dateStr; }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><p className="text-[#475569]">Checking authentication...</p></div>;
  if (!user) return <AdminLogin onLogin={setUser} />;

  const statCards = [
    { label: 'Appointments', value: stats.appointments, icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Lab Orders', value: stats.lab_orders, icon: FlaskConical, color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Contact', value: stats.contact_inquiries, icon: Mail, color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Corporate', value: stats.corporate_inquiries, icon: Building2, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Reports', value: stats.reports, icon: FileText, color: 'text-red-500', bg: 'bg-red-50' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-heading">
              Admin Dashboard
            </h1>
            <p className="text-[#475569]">Welcome, {user.name} ({user.email})</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={handleExport} variant="outline" className="rounded-full" data-testid="export-lab-tests-button">
              <Download className="h-4 w-4 mr-2" /> Export Lab Tests
            </Button>
            <Button onClick={fetchAll} className="bg-[#0A2540] text-white rounded-full" data-testid="refresh-admin-button">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" className="rounded-full border-red-300 text-red-500 hover:bg-red-50" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`stat-card-${card.label.toLowerCase()}`}>
                <div className={`${card.bg} rounded-full w-10 h-10 flex items-center justify-center mb-2`}>
                  <Icon className={`h-5 w-5 ${card.color}`} />
                </div>
                <div className="text-2xl font-bold text-[#0A2540]">{card.value}</div>
                <div className="text-xs text-[#475569]">{card.label}</div>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="appointments" className="bg-white rounded-2xl shadow-sm p-6">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
            <TabsTrigger value="lab-orders" data-testid="tab-lab-orders">Lab Orders</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
            <TabsTrigger value="corporate" data-testid="tab-corporate">Corporate</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            {appointments.length === 0 ? <p className="text-[#475569] text-center py-12">No appointments yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[#475569]">
                    <th className="py-3 px-2">Name</th><th className="py-3 px-2">Phone</th><th className="py-3 px-2">Email</th><th className="py-3 px-2">Service</th><th className="py-3 px-2">Date</th><th className="py-3 px-2">Time</th><th className="py-3 px-2">Created</th><th className="py-3 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {appointments.map((appt) => (
                      <tr key={appt.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-[#0A2540]">{appt.name}</td>
                        <td className="py-3 px-2"><a href={`tel:${appt.phone}`} className="text-[#0EA5E9]">{appt.phone}</a></td>
                        <td className="py-3 px-2">{appt.email}</td>
                        <td className="py-3 px-2">{appt.service}</td>
                        <td className="py-3 px-2">{appt.preferred_date}</td>
                        <td className="py-3 px-2">{appt.preferred_time}</td>
                        <td className="py-3 px-2 text-xs">{formatDate(appt.created_at)}</td>
                        <td className="py-3 px-2"><button onClick={() => handleDelete('appointments', appt.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="lab-orders">
            {labOrders.length === 0 ? <p className="text-[#475569] text-center py-12">No lab orders yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[#475569]">
                    <th className="py-3 px-2">Customer</th><th className="py-3 px-2">Phone</th><th className="py-3 px-2">Tests</th><th className="py-3 px-2">Total</th><th className="py-3 px-2">Home</th><th className="py-3 px-2">Created</th><th className="py-3 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {labOrders.map((order) => (
                      <tr key={order.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-[#0A2540]">{order.customer_name}</td>
                        <td className="py-3 px-2"><a href={`tel:${order.phone}`} className="text-[#0EA5E9]">{order.phone}</a></td>
                        <td className="py-3 px-2">{order.tests?.map((t) => <span key={t.test_name} className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded mr-1 mb-1">{t.test_name}</span>)}</td>
                        <td className="py-3 px-2 font-semibold">&#8377;{order.total_amount}</td>
                        <td className="py-3 px-2">{order.home_collection ? 'Yes' : 'No'}</td>
                        <td className="py-3 px-2 text-xs">{formatDate(order.created_at)}</td>
                        <td className="py-3 px-2"><button onClick={() => handleDelete('lab-orders', order.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="contact">
            {contactInquiries.length === 0 ? <p className="text-[#475569] text-center py-12">No inquiries yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[#475569]">
                    <th className="py-3 px-2">Name</th><th className="py-3 px-2">Email</th><th className="py-3 px-2">Phone</th><th className="py-3 px-2">Subject</th><th className="py-3 px-2">Message</th><th className="py-3 px-2">Created</th><th className="py-3 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {contactInquiries.map((inq) => (
                      <tr key={inq.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-[#0A2540]">{inq.name}</td>
                        <td className="py-3 px-2">{inq.email}</td>
                        <td className="py-3 px-2"><a href={`tel:${inq.phone}`} className="text-[#0EA5E9]">{inq.phone}</a></td>
                        <td className="py-3 px-2">{inq.subject}</td>
                        <td className="py-3 px-2 max-w-xs truncate">{inq.message}</td>
                        <td className="py-3 px-2 text-xs">{formatDate(inq.created_at)}</td>
                        <td className="py-3 px-2"><button onClick={() => handleDelete('contact-inquiries', inq.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="corporate">
            {corporateInquiries.length === 0 ? <p className="text-[#475569] text-center py-12">No inquiries yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[#475569]">
                    <th className="py-3 px-2">Company</th><th className="py-3 px-2">Contact</th><th className="py-3 px-2">Email</th><th className="py-3 px-2">Phone</th><th className="py-3 px-2">Package</th><th className="py-3 px-2">Employees</th><th className="py-3 px-2">Created</th><th className="py-3 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {corporateInquiries.map((inq) => (
                      <tr key={inq.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-[#0A2540]">{inq.company_name}</td>
                        <td className="py-3 px-2">{inq.contact_person}</td>
                        <td className="py-3 px-2">{inq.email}</td>
                        <td className="py-3 px-2"><a href={`tel:${inq.phone}`} className="text-[#0EA5E9]">{inq.phone}</a></td>
                        <td className="py-3 px-2">{inq.package_type}</td>
                        <td className="py-3 px-2">{inq.employee_count || '-'}</td>
                        <td className="py-3 px-2 text-xs">{formatDate(inq.created_at)}</td>
                        <td className="py-3 px-2"><button onClick={() => handleDelete('corporate-inquiries', inq.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="reports">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0A2540]">Patient Reports</h3>
              <Button onClick={() => setShowReportForm(!showReportForm)} className="bg-[#0EA5E9] text-white rounded-full" data-testid="add-report-button">
                <Plus className="h-4 w-4 mr-2" /> Add Report
              </Button>
            </div>

            {showReportForm && (
              <form onSubmit={handleReportSubmit} className="bg-[#F8FAFC] rounded-xl p-6 mb-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Patient Phone *</Label>
                    <Input value={reportForm.patient_phone} onChange={(e) => setReportForm(p => ({ ...p, patient_phone: e.target.value }))} required className="mt-1" data-testid="report-patient-phone" />
                  </div>
                  <div>
                    <Label>Patient Name *</Label>
                    <Input value={reportForm.patient_name} onChange={(e) => setReportForm(p => ({ ...p, patient_name: e.target.value }))} required className="mt-1" data-testid="report-patient-name" />
                  </div>
                  <div>
                    <Label>Report Name *</Label>
                    <Input value={reportForm.report_name} onChange={(e) => setReportForm(p => ({ ...p, report_name: e.target.value }))} required className="mt-1" placeholder="e.g. CBC Report" data-testid="report-name" />
                  </div>
                  <div>
                    <Label>Report Date *</Label>
                    <Input type="date" value={reportForm.report_date} onChange={(e) => setReportForm(p => ({ ...p, report_date: e.target.value }))} required className="mt-1" data-testid="report-date" />
                  </div>
                </div>
                <div>
                  <Label>Report URL (Google Drive / direct link) *</Label>
                  <Input value={reportForm.report_url} onChange={(e) => setReportForm(p => ({ ...p, report_url: e.target.value }))} required className="mt-1" placeholder="https://drive.google.com/..." data-testid="report-url" />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="bg-[#0A2540] text-white rounded-full" data-testid="submit-report-button">Save Report</Button>
                  <Button type="button" variant="outline" onClick={() => setShowReportForm(false)} className="rounded-full">Cancel</Button>
                </div>
              </form>
            )}

            {reports.length === 0 ? <p className="text-[#475569] text-center py-12">No reports uploaded yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-[#475569]">
                    <th className="py-3 px-2">Patient</th><th className="py-3 px-2">Phone</th><th className="py-3 px-2">Report</th><th className="py-3 px-2">Date</th><th className="py-3 px-2">Link</th><th className="py-3 px-2">Created</th><th className="py-3 px-2"></th>
                  </tr></thead>
                  <tbody>
                    {reports.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-[#0A2540]">{r.patient_name}</td>
                        <td className="py-3 px-2">{r.patient_phone}</td>
                        <td className="py-3 px-2">{r.report_name}</td>
                        <td className="py-3 px-2">{r.report_date}</td>
                        <td className="py-3 px-2"><a href={r.report_url} target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline">View</a></td>
                        <td className="py-3 px-2 text-xs">{formatDate(r.created_at)}</td>
                        <td className="py-3 px-2"><button onClick={() => handleDelete('admin/reports', r.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
